import DiagnosticsModule, {
  MetricNames,
  MetricTags,
} from '@modules/DiagnosticsModule';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import {
  RTCSMetricNames,
  RTCSMetricTags,
  RTCSOperationNames,
  RtcsResyncOutcome,
} from '../../metrics/rtcsMetrics';
import getServiceQueues from '../api/getQueues';
import getUsers from '../api/users/getUsers';
import log, { logException } from './log';
import { serviceState } from './state';

/**
 * Max successful resyncs allowed inside {@link RATE_LIMIT_WINDOW_MS}. Prevents
 * a flapping RTCS or bad-network client from hammering the BFF when many
 * reconnect events fire in quick succession.
 */
export const RATE_LIMIT_MAX = 5;
export const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000;

let inFlight: Promise<void> | null = null;
let recentResyncs: number[] = [];

function isRateLimited(): boolean {
  const cutoff = Date.now() - RATE_LIMIT_WINDOW_MS;
  recentResyncs = recentResyncs.filter((t) => t > cutoff);
  return recentResyncs.length >= RATE_LIMIT_MAX;
}

function recordResyncMetrics(outcomeTag: string, startTime: number): void {
  DiagnosticsModule.incrementCounter(
    RTCSMetricNames.RTCS_RESYNC_TRIGGERED_TOTAL,
    {
      [RTCSMetricTags.RTCS_RESYNC_OUTCOME]: outcomeTag,
    },
  );

  DiagnosticsModule.recordHistogram(
    MetricNames.LATENCY_DURATION,
    Date.now() - startTime,
    {
      [MetricTags.OPERATION_NAME]: RTCSOperationNames.RTCS_RESYNC_DURATION,
    },
  );
}

async function executeResync(): Promise<void> {
  const currentUserId = serviceState.id;
  if (!currentUserId) {
    log('workspace:resync:skipped', { reason: 'no-user-id' });
    return;
  }

  const startTime = Date.now();
  recentResyncs.push(startTime);
  log('workspace:resync:start');

  try {
    // Only queues + users are mutated by RTCS events, so only those need
    // resyncing. Live-services mappings are static config loaded at workspace
    // open and stay correct through a reconnect.
    const [queues, users] = await Promise.all([
      getServiceQueues(true),
      getUsers(currentUserId, true),
    ]);

    // Guard against closeWorkspace / user-switch landing between the await
    // and the write. Without this, the previous user's roster would clobber
    // the cleared (or freshly opened) session's serviceState.
    if (serviceState.id !== currentUserId) {
      log('workspace:resync:skipped', { reason: 'user-changed' });
      return;
    }

    serviceState.queues = queues;
    serviceState.users = users;

    recordResyncMetrics(RtcsResyncOutcome.SUCCESS, startTime);
    log('workspace:resync:complete', { durationMs: Date.now() - startTime });
  } catch (err) {
    recordResyncMetrics(RtcsResyncOutcome.ERROR, startTime);
    logException(err, 'workspace:resync:failed');
  }
}

/**
 * Refresh the queues + users cache after the RTCS connection reconnects, so
 * any updates missed during the disconnect window are picked up.
 *
 * Concurrent callers share the in-flight Promise (no duplicate fetches). A
 * sliding-window rate limit (max {@link RATE_LIMIT_MAX} per
 * {@link RATE_LIMIT_WINDOW_MS}) protects against bursts when RTCS is flapping.
 */
export function triggerResync(): Promise<void> {
  if (
    !FeatureFlagModule.isEnabled(FeatureFlagKeys.featureRtcsReconnectResync)
  ) {
    return Promise.resolve();
  }

  if (inFlight) {
    log('workspace:resync:skipped', { reason: 'in-flight' });
    return inFlight;
  }

  if (isRateLimited()) {
    log('workspace:resync:skipped', {
      reason: 'rate-limited',
      recentCount: recentResyncs.length,
      windowMs: RATE_LIMIT_WINDOW_MS,
    });
    return Promise.resolve();
  }

  inFlight = executeResync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Test-only: clear in-flight promise and rate-limit history. */
export function __resetResyncForTests(): void {
  inFlight = null;
  recentResyncs = [];
}
